import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { jobsService, WorkflowJob } from '../services/jobsService';
import { BETA_TOOL_LABELS } from '../constants/betaToolLabels';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
const STORAGE_KEY = 'riskninja-background-reports';
const POLL_INTERVAL = 5000; // 5 seconds
const STALE_REPORT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes - auto-cleanup stale pending reports
// Completed reports older than this fall off the bell automatically so the
// dropdown doesn't accumulate forever. They're still reachable via the "View
// All Jobs" panel.
const COMPLETED_AUTO_CLEAR_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface BackgroundReport {
  workflowId: string;
  workflowType: string;
  customerName: string;
  customerId: string;
  projectId?: string;
  status: 'processing' | 'completed' | 'failed';
  startedAt: string;
  completedAt?: string;
  savedReportId?: string;
  documents?: Array<{ id: string; name: string }>;
}

interface BackgroundReportContextType {
  pendingReports: BackgroundReport[];
  completedReports: BackgroundReport[];
  addBackgroundReport: (report: Omit<BackgroundReport, 'status' | 'startedAt'>) => void;
  removeReport: (workflowId: string) => void;
  clearAllCompleted: () => Promise<void>;
  markNotificationSent: (workflowId: string) => Promise<void>;
  hasActiveBackgroundReport: boolean;
  pendingCount: number;
  completedCount: number;
  totalCount: number;
  refreshReports: () => Promise<void>;
}

const BackgroundReportContext = createContext<BackgroundReportContextType | undefined>(undefined);

export const useBackgroundReports = () => {
  const context = useContext(BackgroundReportContext);
  if (context === undefined) {
    throw new Error('useBackgroundReports must be used within a BackgroundReportProvider');
  }
  return context;
};

interface BackgroundReportProviderProps {
  children: React.ReactNode;
}

export const BackgroundReportProvider: React.FC<BackgroundReportProviderProps> = ({ children }) => {
  const { token, user } = useAuth();
  const { showToast } = useToast();
  const [pendingReports, setPendingReports] = useState<BackgroundReport[]>([]);
  const [completedReports, setCompletedReports] = useState<BackgroundReport[]>([]);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousCompletedIdsRef = useRef<Set<string>>(new Set());
  const clearedIdsRef = useRef<Set<string>>(new Set());
  // Tracks whether we've completed our first poll. The first poll silently
  // seeds previousCompletedIdsRef with whatever is already on the server so
  // backlog notifications don't fire toasts on page load / login. Subsequent
  // polls fire toasts only for genuinely new completions.
  const hasSeededRef = useRef(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.pendingReports) {
          setPendingReports(data.pendingReports);
        }
        if (data.completedReports) {
          setCompletedReports(data.completedReports);
          // Initialize the set of known completed IDs
          data.completedReports.forEach((r: BackgroundReport) => {
            previousCompletedIdsRef.current.add(r.workflowId);
          });
        }
      }
    } catch (error) {
      console.error('Failed to load background reports from localStorage:', error);
    }
  }, []);

  // Save to localStorage whenever reports change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        pendingReports,
        completedReports
      }));
    } catch (error) {
      console.error('Failed to save background reports to localStorage:', error);
    }
  }, [pendingReports, completedReports]);

  // Fetch pending notifications from new jobs API (queue-based)
  const fetchJobsApiNotifications = useCallback(async () => {
    if (!token) return;

    try {
      // Get pending notifications from the new jobs API
      const notificationsResponse = await jobsService.getPendingNotifications(token);

      if (notificationsResponse.success && notificationsResponse.jobs.length > 0) {
        for (const job of notificationsResponse.jobs) {
          if (previousCompletedIdsRef.current.has(job.id)) continue;

          if (hasSeededRef.current) {
            const workflowDisplayName = formatWorkflowType(job.workflowType);
            const customerName = job.customer?.name || 'Unknown Customer';

            showToast({
              type: 'success',
              title: 'Report Ready',
              message: `${workflowDisplayName} for ${customerName} is complete.`,
              duration: 10000,
              action: job.savedReportId ? {
                label: 'View Report',
                onClick: () => {
                  window.location.href = `/workflow-report?reportId=${job.savedReportId}`;
                }
              } : undefined
            });
          }

          previousCompletedIdsRef.current.add(job.id);

          // NOTE: Do NOT mark notification as sent here. The notification should
          // remain visible in the bell until the user explicitly clicks "View"
          // in the notification dropdown. markNotificationSent is called in
          // NotificationCenter.handleViewReport() when user views the report.
        }
      }

      // Also fetch active jobs to update pending reports (include completed to get all statuses)
      const activeJobsResponse = await jobsService.getUserJobs(token, true);

      if (activeJobsResponse.success) {
        // Convert jobs to BackgroundReport format
        const jobsAsPending: BackgroundReport[] = activeJobsResponse.jobs
          .filter(job => job.status === 'pending' || job.status === 'processing')
          .map(job => ({
            workflowId: job.id,
            workflowType: job.workflowType,
            customerName: job.customer?.name || 'Unknown Customer',
            customerId: job.customerId || '',
            projectId: job.projectId || undefined,
            status: 'processing' as const,
            startedAt: job.createdAt,
            savedReportId: job.savedReportId || undefined,
          }));

        // 24h auto-clear: drop completed jobs whose completion (or creation,
        // if no completedAt) is older than the threshold so the bell doesn't
        // accumulate forever. The full history is still in the Jobs panel.
        const completedCutoff = Date.now() - COMPLETED_AUTO_CLEAR_MS;
        const jobsAsCompleted: BackgroundReport[] = activeJobsResponse.jobs
          .filter(job =>
            job.status === 'completed' &&
            !job.notificationSent &&
            !clearedIdsRef.current.has(job.id) &&
            new Date(job.completedAt || job.createdAt).getTime() >= completedCutoff
          )
          .map(job => ({
            workflowId: job.id,
            workflowType: job.workflowType,
            customerName: job.customer?.name || 'Unknown Customer',
            customerId: job.customerId || '',
            projectId: job.projectId || undefined,
            status: 'completed' as const,
            startedAt: job.createdAt,
            completedAt: job.completedAt || undefined,
            savedReportId: job.savedReportId || undefined,
          }));

        // Return true to indicate we got data from jobs API
        // When using jobs API, trust it as the source of truth
        // Only keep very recent local-only reports (less than 30 seconds old) that might not have synced yet
        const now = new Date();
        setPendingReports(prev => {
          const recentLocalOnlyReports = prev.filter(r => {
            // Only keep local-only reports (workflow_ prefix) that are very recent
            if (!r.workflowId.startsWith('workflow_')) return false;
            const reportAge = now.getTime() - new Date(r.startedAt).getTime();
            return reportAge < 30000; // 30 seconds
          });
          return [...jobsAsPending, ...recentLocalOnlyReports];
        });

        setCompletedReports(jobsAsCompleted);
        return true;
      }
    } catch (error) {
      // Jobs API not available, fall back to old workflow API
      console.log('Jobs API not available, using workflow API fallback');
    }

    return false;
  }, [token, showToast]);

  // Fetch pending reports from server (legacy workflow API).
  // Returns true only when the poll actually succeeded, so the caller can
  // decide whether it's safe to flip the seed flag.
  const fetchPendingReports = useCallback(async (): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/background/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch background reports');
      }

      const data = await response.json();

      if (data.success) {
        // Map server data to our format
        const serverPending: BackgroundReport[] = data.pending.map((w: any) => ({
          workflowId: w.workflowId,
          workflowType: w.workflowType,
          customerName: w.customerName,
          customerId: w.customerId,
          projectId: w.projectId,
          status: 'processing' as const,
          startedAt: w.startedAt,
          savedReportId: w.savedReportId,
          documents: w.stepData?.documentsUsed?.map((id: string) => ({ id, name: 'Document' })) || []
        }));

        // 24h auto-clear (see jobs-API branch above for rationale).
        const completedCutoff = Date.now() - COMPLETED_AUTO_CLEAR_MS;
        const serverCompleted: BackgroundReport[] = data.completed
          .filter((w: any) => new Date(w.completedAt || w.startedAt).getTime() >= completedCutoff)
          .map((w: any) => ({
            workflowId: w.workflowId,
            workflowType: w.workflowType,
            customerName: w.customerName,
            customerId: w.customerId,
            projectId: w.projectId,
            status: 'completed' as const,
            startedAt: w.startedAt,
            completedAt: w.completedAt || undefined,
            savedReportId: w.savedReportId,
            documents: w.stepData?.documentsUsed?.map((id: string) => ({ id, name: 'Document' })) || []
          }));

        // Check for newly completed reports to show toast notifications.
        // First poll silently seeds the known-set so backlog notifications
        // don't all fire on page load / login.
        serverCompleted.forEach((report) => {
          if (previousCompletedIdsRef.current.has(report.workflowId)) return;

          if (hasSeededRef.current) {
            const workflowDisplayName = formatWorkflowType(report.workflowType);
            showToast({
              type: 'success',
              title: 'Report Ready',
              message: `${workflowDisplayName} for ${report.customerName} is complete.`,
              duration: 10000,
              action: report.savedReportId ? {
                label: 'View Report',
                onClick: () => {
                  window.location.href = `/workflow-report?reportId=${report.savedReportId}`;
                }
              } : undefined
            });
          }
          previousCompletedIdsRef.current.add(report.workflowId);

          // NOTE: Do NOT mark notification as sent here. The notification should
          // remain visible in the bell until the user explicitly clicks "View"
          // in the notification dropdown.
        });

        // IMPORTANT: Merge local reports with server reports instead of replacing
        // This preserves locally-added reports (from child component workflows) that
        // may not have a matching server record yet
        setPendingReports((currentPending) => {
          // Keep local reports that have temporary IDs (start with 'workflow_')
          // These are from child component workflows that don't have server records
          const localOnlyReports = currentPending.filter(
            r => r.workflowId.startsWith('workflow_') &&
                 !serverPending.some(sr => sr.workflowId === r.workflowId)
          );

          // Check if any local reports should be marked as completed
          // by matching customer + workflow type to server completed reports
          const now = new Date();
          const stillPendingLocalReports = localOnlyReports.filter(localReport => {
            // Auto-cleanup stale reports (older than 30 minutes)
            const reportAge = now.getTime() - new Date(localReport.startedAt).getTime();
            if (reportAge > STALE_REPORT_TIMEOUT_MS) {
              console.log(`🧹 Auto-removing stale report: ${localReport.workflowId} (age: ${Math.round(reportAge / 60000)}m)`);
              return false; // Remove stale report
            }

            const matchingCompleted = serverCompleted.find(
              sr => sr.customerId === localReport.customerId &&
                   sr.workflowType === localReport.workflowType &&
                   new Date(sr.startedAt) >= new Date(localReport.startedAt)
            );
            if (matchingCompleted) {
              // This local report has a matching completed report on the server
              // Show toast and mark as done
              if (!previousCompletedIdsRef.current.has(localReport.workflowId)) {
                const workflowDisplayName = formatWorkflowType(localReport.workflowType);
                showToast({
                  type: 'success',
                  title: 'Report Ready',
                  message: `${workflowDisplayName} for ${localReport.customerName} is complete.`,
                  duration: 10000,
                  action: matchingCompleted.savedReportId ? {
                    label: 'View Report',
                    onClick: () => {
                      window.location.href = `/workflow-report?reportId=${matchingCompleted.savedReportId}`;
                    }
                  } : undefined
                });
                previousCompletedIdsRef.current.add(localReport.workflowId);
              }
              return false; // Remove from pending
            }
            return true; // Keep in pending
          });

          // Combine server pending with remaining local pending
          return [...serverPending, ...stillPendingLocalReports];
        });

        setCompletedReports(serverCompleted);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error fetching background reports:', error);
      return false;
    }
  }, [token, showToast]);

  // Mark notification as sent on server
  const markNotificationSentOnServer = async (workflowId: string) => {
    if (!token) return;

    try {
      await fetch(`${API_BASE_URL}/api/workflows/${workflowId}/notification-sent`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error marking notification sent:', error);
    }
  };

  // Combined fetch function that tries jobs API first, then falls back to workflow API
  const fetchAllReports = useCallback(async () => {
    // Try the new jobs API first
    const jobsApiWorked = await fetchJobsApiNotifications();

    // If jobs API didn't work, fall back to legacy workflow API
    let pollSucceeded = jobsApiWorked;
    if (!jobsApiWorked) {
      pollSucceeded = await fetchPendingReports();
    }

    // The first SUCCESSFUL poll seeds previousCompletedIdsRef with whatever is
    // already on the server; toasts only fire from the next poll onward. We
    // must NOT flip this flag on a failed poll — at login the first request can
    // briefly 401/race before the token settles, and seeding "true" without
    // having recorded the existing completions makes the next successful poll
    // fire a toast for every backlog report. Gate strictly on success.
    if (pollSucceeded && !hasSeededRef.current) {
      hasSeededRef.current = true;
    }
  }, [fetchJobsApiNotifications, fetchPendingReports]);

  // Start polling when user is authenticated and there are pending reports
  useEffect(() => {
    if (token && user) {
      // Initial fetch
      fetchAllReports();

      // Set up polling
      pollIntervalRef.current = setInterval(fetchAllReports, POLL_INTERVAL);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [token, user, fetchAllReports]);

  const addBackgroundReport = useCallback((report: Omit<BackgroundReport, 'status' | 'startedAt'>) => {
    const newReport: BackgroundReport = {
      ...report,
      status: 'processing',
      startedAt: new Date().toISOString()
    };

    setPendingReports((prev) => {
      // Don't add duplicate
      if (prev.some(r => r.workflowId === newReport.workflowId)) {
        return prev;
      }
      return [...prev, newReport];
    });
  }, []);

  const removeReport = useCallback((workflowId: string) => {
    setPendingReports((prev) => prev.filter(r => r.workflowId !== workflowId));
    setCompletedReports((prev) => prev.filter(r => r.workflowId !== workflowId));
  }, []);

  const markNotificationSent = useCallback(async (workflowId: string) => {
    // Suppress from poller immediately
    clearedIdsRef.current.add(workflowId);
    // Remove from completed reports after notification is acknowledged
    setCompletedReports((prev) => prev.filter(r => r.workflowId !== workflowId));

    // Mark on server via jobs API (primary) and legacy API (fallback)
    try {
      await jobsService.markNotificationSent(token || '', workflowId);
    } catch {
      await markNotificationSentOnServer(workflowId);
    }

    // Clear suppression after server has been updated
    setTimeout(() => {
      clearedIdsRef.current.delete(workflowId);
    }, POLL_INTERVAL * 2);
  }, [token]);

  const clearAllCompleted = useCallback(async () => {
    // Track cleared IDs locally so the poller doesn't re-add them before server updates
    const idsToMark = completedReports.map(r => r.workflowId);
    idsToMark.forEach(id => clearedIdsRef.current.add(id));

    // Clear completed reports from state immediately
    setCompletedReports([]);

    // Mark all completed reports as notified on server using jobs API
    const promises = completedReports.map(report =>
      jobsService.markNotificationSent(token || '', report.workflowId).catch(err => {
        console.warn(`Failed to mark notification for ${report.workflowId}:`, err);
        // Also try legacy endpoint as fallback
        markNotificationSentOnServer(report.workflowId).catch(() => {});
      })
    );
    await Promise.all(promises);

    // Clear the local suppression after server has been updated
    setTimeout(() => {
      idsToMark.forEach(id => clearedIdsRef.current.delete(id));
    }, POLL_INTERVAL * 2);
  }, [completedReports, token]);

  const refreshReports = useCallback(async () => {
    await fetchAllReports();
  }, [fetchAllReports]);

  const hasActiveBackgroundReport = pendingReports.length > 0;
  const pendingCount = pendingReports.length;
  const completedCount = completedReports.length;
  const totalCount = pendingCount + completedCount;

  return (
    <BackgroundReportContext.Provider
      value={{
        pendingReports,
        completedReports,
        addBackgroundReport,
        removeReport,
        clearAllCompleted,
        markNotificationSent,
        hasActiveBackgroundReport,
        pendingCount,
        completedCount,
        totalCount,
        refreshReports
      }}
    >
      {children}
    </BackgroundReportContext.Provider>
  );
};

// Helper function to format workflow type for display
function formatWorkflowType(workflowType: string): string {
  const typeMap: Record<string, string> = {
    ...BETA_TOOL_LABELS,
    'loss_run_analysis': 'Loss Run Analysis',
    'loss_run_analyzer': 'Loss Run Analyzer',
    'loss_run_analyzer_v4': 'Loss Run Analyzer (legacy NEW)',
    'loss_run_analyzer_v4_3': 'Loss Run Analysis',
    'renewal_analysis': 'Renewal Analysis',
    'year_over_year': 'Year Over Year',
    'yoy_renewal': 'YOY Renewal',
    'personal_lines_yoy_renewal': 'Personal Lines YOY',
    'commercial_lines_exec_renewal': 'Commercial Lines Exec',
    'coverage_analysis': 'Coverage Analysis',
    'insurance_requirements': 'Insurance Requirements',
    'document_comparison': 'Document Comparison',
    'research': 'Research',
    'policy_comparison': 'Policy Comparison',
    'financial_analysis': 'Financial Analysis',
    'client_renewal_presentation': 'Client Renewal Presentation',
    'certificate_of_effort': 'Certificate of Effort',
    'universal_analysis': 'Universal Analysis',
    'rate_analysis': 'Rate Analysis',
    'portfolio_analyzer': 'Portfolio Analyzer',
    'homeowners_comparison': 'Homeowners Insurance Comparison',
    'personal_umbrella': 'Personal Umbrella Analysis',
  };

  return typeMap[workflowType] || workflowType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
